import styled, { CSSObject } from "@emotion/styled";
import React from "react";

import {
  GradientDefinitionSVG,
  IconPicker,
} from "@brexhq/next/src/components/building-blocks/IconPicker/IconPicker";
import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import BlockRenderer from "@brexhq/next/src/components/shared/BlockRenderer";
import styles from "@brexhq/shared/components/Typography/styles";
import { defaultTheme } from "@brexhq/shared/styles/theming";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { SanityListProps } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";
import { ThemeTextComponents } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/TextRow";

export interface ListProps extends SanityListProps {
  theme: ModuleThemeType;
}

type StyledUlProps = Required<
  Pick<ListProps, "verticalSpacing" | "primaryTextScale">
>;

type TextScale = NonNullable<ListProps["primaryTextScale"]>;

const getResponsiveRulesForScale = (scale: TextScale): CSSObject => {
  const { components } = ThemeTextComponents[`Body ${scale}`];
  return {
    [defaultTheme.breakpoints.LRG]: {
      fontSize: styles[components.LRG].fontSize,
      lineHeight: styles[components.LRG].lineHeight,
    },
    [defaultTheme.breakpoints.MED]: {
      fontSize: styles[components.MED].fontSize,
      lineHeight: styles[components.MED].lineHeight,
    },
    [defaultTheme.breakpoints.SML]: {
      fontSize: styles[components.SML].fontSize,
      lineHeight: styles[components.SML].lineHeight,
    },
  };
};

const responsiveRulesByScale: Record<
  NonNullable<ListProps["primaryTextScale"]>,
  CSSObject
> = {
  S: getResponsiveRulesForScale("S"),
  M: getResponsiveRulesForScale("M"),
  L: getResponsiveRulesForScale("L"),
};

const StyledUl = styled.ul<StyledUlProps>(
  ({ verticalSpacing, primaryTextScale, theme }) => {
    return {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing.LRG[verticalSpacing],
      padding: "0",
      ...responsiveRulesByScale[primaryTextScale],
    };
  },
);

const ListItem = styled.li<{
  listStyle: ListProps["style"];
  gap: ListProps["gap"];
}>(({ listStyle, gap, theme, color }) => {
  const bulletStyle = {
    //eslint-disable-next-line quotes
    content: `"•"`,
    color: color ?? "inherit",
    fontWeight: "bold",
    display: "inline-block",
    height: "100%",
    flexShrink: 0,
    lineHeight: "inherit",
  };

  return {
    display: "flex",
    alignItems: listStyle === "bullet" ? "flex-start" : "center",
    listStyleType: "none",
    gap: theme.spacing.LRG[gap],
    //using before instead of marker because there isn't enough styling available to align
    ["::before"]: listStyle === "bullet" ? bulletStyle : {},
  };
});

const NoShrink = styled.div({
  flexShrink: 0,
});

export const List: React.FC<ListProps> = ({
  theme,
  gap,
  iconSize,
  items,
  marginTop,
  style,
  verticalSpacing,
  primaryTextScale = "L",
  secondaryTextScale = "M",
}) => {
  const { standard } = getModuleThemeColors(theme);
  return (
    <Container margin={[marginTop ?? 0, 0, 0, 0]} width="100%">
      <StyledUl
        primaryTextScale={primaryTextScale}
        verticalSpacing={verticalSpacing}
      >
        {style === "icon" &&
          items.some((item) => item.color === "gradientPeachCyan") && (
            <GradientDefinitionSVG />
          )}
        {items.map(({ _key, primaryText, secondaryText, icon, color }) => (
          <ListItem
            key={_key}
            listStyle={style}
            gap={gap}
            color={standard.textPrimary}
          >
            {icon && style === "icon" && (
              <NoShrink>
                <IconPicker
                  theme={theme}
                  marginTop="none"
                  size={{
                    LRG: iconSize,
                    MED: iconSize,
                    SML: iconSize,
                  }}
                  icon={icon}
                  color={color ?? "single"}
                />
              </NoShrink>
            )}

            <Container>
              <BlockRenderer
                blocks={primaryText}
                theme={standard}
                typeScale={primaryTextScale}
                colorOverwrite="primary"
                component="list"
              />
              {secondaryText && (
                <BlockRenderer
                  blocks={secondaryText}
                  theme={standard}
                  typeScale={secondaryTextScale}
                  component="list"
                />
              )}
            </Container>
          </ListItem>
        ))}
      </StyledUl>
    </Container>
  );
};
